import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, X } from "lucide-react";
import { toast } from "sonner";

import { POLICY_KIND_CHIP_CLASS } from "@/components/Policy/PolicyKindChips";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TradeFilterSelectFullWidth, FieldSelectFit } from "@/components/trade-filter-selects";
import { cn } from "@/lib/utils";
import { checkPolicyPickOverlap } from "../../../services/reportsApi";
import type { Backtest } from "../../../types/backtests";
import type {
  BundlePreview,
  PolicyPickOverlapAction,
  PolicyPickOverlapResponse,
} from "../../../types/policyPickOverlap";
import type { TradePolicyCatalogField } from "../../../types/tradePolicy";
import { isPolicyTreeV2 } from "../../../types/tradePolicy";
import { presentMutationError } from "../../../utils/showApiErrorToast";
import type { ReportPick } from "../../../utils/reportPickListStorage";
import { isExitPlacementPick } from "../../../utils/exitPlacementPicks";
import {
  legAvailabilityFromExitPlacementPick,
  legAvailabilityFromPolicyRules,
  normalizeSimulateLegs,
  normalizeSimulatedLegs,
  type ExitPlacementLegAvailability,
} from "../../../utils/exitPlacementSimLegs";
import {
  MAX_POLICY_PICK_OVERLAP,
  buildOverlapRequestFromSelection,
  catalogCtxFromFields,
  isExitPlacementBacktest,
  overlapPickIdsFromSelection,
  resolveRunnableAtomicIdsFromOverlapPickIds,
  selectionItemFromBacktest,
  selectionItemFromReportPick,
  type OverlapSelectionItem,
} from "../../../utils/policyPickOverlapMappers";
import {
  REPORTS_DETAIL_FIELD_DESC_CLASS,
  REPORTS_DETAIL_MUTED_TEXT_CLASS,
} from "../Reports/reportsDetailTheme";
import {
  baselineSummaryLine,
  bundlePreviewHasBlockingError,
  complementarityTooltip,
  entryFilterImpactSummary,
  exitPlacementDeltaPnlLine,
  exitPlacementImpactSummary,
  findBundlePreviewForPickIds,
  formatOverlapMoney,
  formatOverlapPct,
  isExitPlacementImpact,
  isExitPlacementPickResult,
  overlapCellColor,
  overlapImpactKindBadgeLabel,
  pairwiseOverlapBreakdown,
  pickLabelById,
  pickResultImpactSummary,
  resolveOverlapPickDisplayLabel,
  suggestionImpactLine,
  unionPickIdsFromSuggestions,
} from "./policyPickOverlapPresentation";
import { SimulatorAlert } from "./SimulatorAlert";

const FIELD_LABEL_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal text-[color:var(--brand-tropical-surf)]";

const BODY_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal leading-relaxed text-gray-700 dark:text-sidebar-foreground";

const MUTED_CLASS = `font-[family-name:var(--font-primary)] text-sm font-normal leading-relaxed ${REPORTS_DETAIL_MUTED_TEXT_CLASS}`;

const SECTION_PANEL_CLASS =
  "rounded-[6px] border border-solid border-border bg-muted/40 p-3 dark:bg-sidebar/40";

const REGIME_ACTION_OPTIONS = [
  { value: "veto", label: "Remove" },
  { value: "keep_only", label: "Keep" },
];

export type PolicyPickOverlapDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reportId: string;
  reportPicks: ReportPick[];
  linkedBacktests: Backtest[];
  catalogFields: TradePolicyCatalogField[];
  marginalEqPrefixes?: Record<string, string>;
  defaultTargetAction?: PolicyPickOverlapAction | null;
  initialSelection?: OverlapSelectionItem[];
  pickToBacktestId?: Map<string, string>;
  mode?: "regime" | "exit_placement";
  /**
   * Where Check overlap was opened from.
   * - `picks` (kanban): selection is report picks — do not offer “Add saved sim”.
   * - `saved_sims` (comparison): selection is saved sims — do not offer “Add from picks”.
   */
  entry?: "picks" | "saved_sims";
  onRunCombinedSim?: (pickIds: string[], targetAction: PolicyPickOverlapAction) => void;
  /** Run each checked Top suggestion as its own combined simulation. */
  onRunSuggestionSims?: (
    combos: Array<{ pickIds: string[]; action: PolicyPickOverlapAction; title: string }>
  ) => void;
  onRunCombinedExitPlacementSim?: (pickIds: string[]) => void;
};

export function PolicyPickOverlapDialog(props: PolicyPickOverlapDialogProps): React.ReactElement {
  const isExitPlacementMode = props.mode === "exit_placement";
  const entry = props.entry ?? "picks";
  const showAddPicks = entry !== "saved_sims";
  const showAddSavedSims = entry !== "picks";
  const [selection, setSelection] = useState<OverlapSelectionItem[]>([]);
  const defaultRegimeAction = props.defaultTargetAction ?? "veto";
  const [actionByPickId, setActionByPickId] = useState<Record<string, PolicyPickOverlapAction>>(
    {}
  );
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<PolicyPickOverlapResponse | null>(null);
  const [selectedComboPickIds, setSelectedComboPickIds] = useState<string[] | null>(null);
  /** Ranks of Top suggestions checked for the custom combination preview. */
  const [selectedSuggestionRanks, setSelectedSuggestionRanks] = useState<Set<number>>(
    () => new Set()
  );
  const [skippedNotices, setSkippedNotices] = useState<Array<{ label: string; reason: string }>>([]);
  /** Remount add-menus after pick so they return to placeholder. */
  const [addPickMenuKey, setAddPickMenuKey] = useState(0);
  const [addSimMenuKey, setAddSimMenuKey] = useState(0);

  const fieldsByPath = useMemo(() => {
    const m = new Map<string, TradePolicyCatalogField>();
    for (const f of props.catalogFields) m.set(f.path, f);
    return m;
  }, [props.catalogFields]);

  const catalogCtx = useMemo(
    () => catalogCtxFromFields(fieldsByPath, props.marginalEqPrefixes),
    [fieldsByPath, props.marginalEqPrefixes]
  );

  const reportPicksById = useMemo(
    () => new Map(props.reportPicks.map((p) => [p.id, p])),
    [props.reportPicks]
  );

  const backtestsById = useMemo(
    () => new Map(props.linkedBacktests.map((b) => [b.id, b])),
    [props.linkedBacktests]
  );

  const resolveExitAvailabilityForPick = useCallback(
    (pickId: string, backtestId?: string): ExitPlacementLegAvailability | null => {
      const reportPick = reportPicksById.get(pickId);
      if (reportPick && isExitPlacementPick(reportPick)) {
        return legAvailabilityFromExitPlacementPick(reportPick);
      }
      const btKey =
        backtestId?.trim() || (pickId.startsWith("bt-") ? pickId.slice(3) : "");
      const bt = btKey ? backtestsById.get(btKey) : undefined;
      if (!bt) return null;
      if (isPolicyTreeV2(bt.policy_tree)) {
        return legAvailabilityFromPolicyRules(bt.policy_tree.rules);
      }
      const scope =
        bt.scope && typeof bt.scope === "object" && !Array.isArray(bt.scope)
          ? (bt.scope as Record<string, unknown>)
          : null;
      if (!scope) return null;
      const simulateLegs = normalizeSimulateLegs(scope.simulate_legs);
      if (simulateLegs === "both") return { hasSl: true, hasTp: true };
      if (simulateLegs === "sl_only") return { hasSl: true, hasTp: false };
      if (simulateLegs === "tp_only") return { hasSl: false, hasTp: true };
      const simulated = normalizeSimulatedLegs(scope.simulated_legs ?? scope.simulate_legs);
      if (simulated?.length) {
        return { hasSl: simulated.includes("sl"), hasTp: simulated.includes("tp") };
      }
      return null;
    },
    [backtestsById, reportPicksById]
  );

  const availableReportPicks = useMemo(() => {
    const selected = new Set(selection.map((s) => s.key));
    return props.reportPicks.filter((p) => !selected.has(`pick:${p.id}`));
  }, [props.reportPicks, selection]);

  const availableBacktests = useMemo(() => {
    const selected = new Set(selection.map((s) => s.key));
    return props.linkedBacktests.filter((b) => !selected.has(`bt:${b.id}`));
  }, [props.linkedBacktests, selection]);

  useEffect(() => {
    if (!props.open) return;
    setSelection(props.initialSelection ?? []);
    setActionByPickId({});
    setResponse(null);
    setSelectedComboPickIds(null);
    setSelectedSuggestionRanks(new Set());
    setSkippedNotices([]);
  }, [props.open, props.initialSelection, props.defaultTargetAction]);

  /** Ensure every regime report pick has an action; drop actions for removed picks. */
  useEffect(() => {
    setActionByPickId((prev) => {
      const next: Record<string, PolicyPickOverlapAction> = {};
      let changed = false;
      for (const item of selection) {
        if (item.sourceKind !== "report_pick" || !item.reportPickId) continue;
        const pick = reportPicksById.get(item.reportPickId);
        if (!pick || isExitPlacementPick(pick)) continue;
        const existing = prev[item.reportPickId];
        next[item.reportPickId] = existing ?? defaultRegimeAction;
        if (existing == null) changed = true;
      }
      for (const key of Object.keys(prev)) {
        if (!(key in next)) changed = true;
      }
      if (!changed && Object.keys(prev).length === Object.keys(next).length) {
        let same = true;
        for (const key of Object.keys(next)) {
          if (prev[key] !== next[key]) {
            same = false;
            break;
          }
        }
        if (same) return prev;
      }
      return next;
    });
  }, [defaultRegimeAction, reportPicksById, selection]);

  const setPickAction = useCallback(
    (pickId: string, action: PolicyPickOverlapAction) => {
      setActionByPickId((prev) => {
        if (prev[pickId] === action) return prev;
        return { ...prev, [pickId]: action };
      });
      setResponse((prev) => {
        if (!prev) return prev;
        toast.info("Action updated — run Check overlap again");
        return null;
      });
      setSelectedComboPickIds(null);
      setSelectedSuggestionRanks(new Set());
    },
    []
  );

  const addReportPick = useCallback(
    (pickId: string) => {
      const pick = reportPicksById.get(pickId);
      if (!pick) return;
      if (selection.length >= MAX_POLICY_PICK_OVERLAP) {
        toast.info(`Maximum ${MAX_POLICY_PICK_OVERLAP} picks`);
        return;
      }
      const linked = props.pickToBacktestId?.get(pickId);
      const item = selectionItemFromReportPick(
        pick,
        linked ? { linkedBacktestId: linked } : undefined
      );
      if (selection.some((s) => s.key === item.key)) return;
      setSelection((prev) => [...prev, item]);
      setResponse(null);
      setSelectedComboPickIds(null);
      setSelectedSuggestionRanks(new Set());
    },
    [props.pickToBacktestId, reportPicksById, selection]
  );

  const addBacktest = useCallback(
    (backtestId: string) => {
      const bt = backtestsById.get(backtestId);
      if (!bt) return;
      if (selection.length >= MAX_POLICY_PICK_OVERLAP) {
        toast.info(`Maximum ${MAX_POLICY_PICK_OVERLAP} picks`);
        return;
      }
      const item = selectionItemFromBacktest(bt);
      if (selection.some((s) => s.key === item.key)) return;
      setSelection((prev) => [...prev, item]);
      setResponse(null);
      setSelectedComboPickIds(null);
      setSelectedSuggestionRanks(new Set());
    },
    [backtestsById, selection]
  );

  const removeSelection = useCallback((key: string) => {
    setSelection((prev) => prev.filter((s) => s.key !== key));
    setResponse(null);
    setSelectedComboPickIds(null);
    setSelectedSuggestionRanks(new Set());
  }, []);

  const runOverlapCheck = useCallback(
    async (previewComboOverride?: string[] | null) => {
      if (selection.length < 1) {
        toast.info("Add at least one pick or saved simulation");
        return;
      }

      const comboIds = previewComboOverride ?? selectedComboPickIds;

      const { requestPicks, skipped } = buildOverlapRequestFromSelection(selection, {
        reportPicksById,
        backtestsById,
        targetAction: defaultRegimeAction,
        actionByPickId,
        catalogCtx,
        pickToBacktestId: props.pickToBacktestId,
      });

      if (requestPicks.length < 1) {
        toast.warning("No valid picks to compare", {
          description: skipped[0]?.reason ?? "Fix pick mapping and try again.",
        });
        return;
      }

      setSkippedNotices(skipped.map((s) => ({ label: s.item.label, reason: s.reason })));
      setLoading(true);
      try {
        const result = await checkPolicyPickOverlap(props.reportId, {
          picks: requestPicks,
          ...(comboIds && comboIds.length >= 2 ? { preview_combinations: [comboIds] } : {}),
        });
        setResponse(result);
        if (comboIds && comboIds.length >= 2) {
          setSelectedComboPickIds(comboIds);
        } else if (result.combination_suggestions.length > 0) {
          const first = result.combination_suggestions[0]!;
          setSelectedComboPickIds(first.pick_ids);
          setSelectedSuggestionRanks(new Set([first.rank]));
        } else if (result.recommended_pick_ids.length >= 2) {
          setSelectedComboPickIds(result.recommended_pick_ids);
          setSelectedSuggestionRanks(new Set());
        }
      } catch (e) {
        const isNetworkError =
          e instanceof TypeError ||
          (e instanceof Error && /failed to fetch|networkerror|load failed/i.test(e.message));
        presentMutationError(
          e,
          isNetworkError
            ? "Couldn't reach Tessera. Check your internet connection and try again."
            : "Overlap check failed"
        );
        setResponse(null);
      } finally {
        setLoading(false);
      }
    },
    [
      actionByPickId,
      backtestsById,
      catalogCtx,
      defaultRegimeAction,
      props.pickToBacktestId,
      props.reportId,
      reportPicksById,
      selectedComboPickIds,
      selection,
    ]
  );

  const applySuggestionSelection = useCallback(
    (nextRanks: Set<number>) => {
      setSelectedSuggestionRanks(nextRanks);
      if (!response?.combination_suggestions.length) {
        setSelectedComboPickIds(null);
        return;
      }
      const unionIds = unionPickIdsFromSuggestions(response.combination_suggestions, nextRanks);
      if (unionIds.length < 2) {
        setSelectedComboPickIds(unionIds.length ? unionIds : null);
        if (nextRanks.size > 0 && unionIds.length < 2) {
          toast.info("Select suggestions that cover at least two picks");
        }
        return;
      }
      setSelectedComboPickIds(unionIds);
      void runOverlapCheck(unionIds);
    },
    [response?.combination_suggestions, runOverlapCheck]
  );

  const toggleSuggestionRank = useCallback(
    (rank: number, checked: boolean) => {
      const next = new Set(selectedSuggestionRanks);
      if (checked) next.add(rank);
      else next.delete(rank);
      applySuggestionSelection(next);
    },
    [applySuggestionSelection, selectedSuggestionRanks]
  );

  const activeBundle = useMemo((): BundlePreview | undefined => {
    if (!response || !selectedComboPickIds?.length) return undefined;
    return findBundlePreviewForPickIds(response.bundle_previews, selectedComboPickIds);
  }, [response, selectedComboPickIds]);

  const driftWarnings = useMemo(() => {
    if (!response) return [];
    return response.picks.flatMap((p) =>
      (p.warnings ?? []).map((w) => ({ pickId: p.pick_id, label: p.label, warning: w }))
    );
  }, [response]);

  const selectionLabelByPickId = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of selection) {
      if (s.reportPickId) map.set(s.reportPickId, s.label);
      if (s.bundleId) map.set(s.bundleId, s.label);
      if (s.backtestId) {
        map.set(`bt-${s.backtestId}`, s.label);
        map.set(s.backtestId, s.label);
      }
    }
    return map;
  }, [selection]);

  const pickLabels = useMemo(() => {
    if (!response) return new Map<string, string>();
    return new Map(
      response.picks.map((p) => [
        p.pick_id,
        resolveOverlapPickDisplayLabel(
          p.label,
          selectionLabelByPickId.get(p.pick_id) ??
            (p.backtest_id ? selectionLabelByPickId.get(p.backtest_id) : undefined)
        ),
      ])
    );
  }, [response, selectionLabelByPickId]);

  const reportPickIdSet = useMemo(
    () => new Set(props.reportPicks.map((p) => p.id)),
    [props.reportPicks]
  );

  const selectionBundleIdSet = useMemo(
    () =>
      new Set(selection.map((s) => s.bundleId).filter((id): id is string => Boolean(id?.trim()))),
    [selection]
  );

  const resolveRunnableIds = useCallback(
    (pickIds: string[]) =>
      resolveRunnableAtomicIdsFromOverlapPickIds(pickIds, {
        selection,
        reportPickIds: reportPickIdSet,
        bundleIds: selectionBundleIdSet,
      }),
    [reportPickIdSet, selection, selectionBundleIdSet]
  );

  const runCombinedDisabledReason = useMemo((): string | null => {
    if (!response) return "Run Check overlap first";
    if (!selectedComboPickIds || selectedComboPickIds.length < 2) {
      return "Select a combination with at least two picks";
    }
    if (!activeBundle?.combine_preview_ok) {
      return isExitPlacementMode
        ? "These contexts overlap — run separately or pick a different combination"
        : "This combination cannot be combined";
    }
    if (!isExitPlacementMode && activeBundle && bundlePreviewHasBlockingError(activeBundle)) {
      return "Keep + Keep conflict blocks combine";
    }
    const runnableIds = resolveRunnableIds(selectedComboPickIds);
    if (runnableIds.length < 2) {
      return isExitPlacementMode
        ? "Needs two exit placement picks in your pick list (saved sims alone cannot run combined here)"
        : "Needs two picks from your board (singles or combined cards) to run";
    }
    if (!isExitPlacementMode) {
      const leafIds = runnableIds.filter((id) => reportPickIdSet.has(id));
      if (leafIds.length > 0) {
        const actions = new Set(
          leafIds.map((id) => actionByPickId[id] ?? defaultRegimeAction)
        );
        if (actions.size > 1) {
          return "Selected picks mix Remove and Keep — set them to the same action to run combined";
        }
      }
    }
    return null;
  }, [
    actionByPickId,
    activeBundle,
    defaultRegimeAction,
    isExitPlacementMode,
    reportPickIdSet,
    resolveRunnableIds,
    response,
    selectedComboPickIds,
  ]);

  const canRunCombined = runCombinedDisabledReason === null;

  const suggestionRunCombos = useMemo(() => {
    if (!response?.combination_suggestions.length || selectedSuggestionRanks.size === 0) {
      return [] as Array<{ pickIds: string[]; action: PolicyPickOverlapAction; title: string; rank: number }>;
    }
    const out: Array<{
      pickIds: string[];
      action: PolicyPickOverlapAction;
      title: string;
      rank: number;
    }> = [];
    for (const suggestion of response.combination_suggestions) {
      if (!selectedSuggestionRanks.has(suggestion.rank)) continue;
      const runnableIds = resolveRunnableIds(suggestion.pick_ids);
      if (runnableIds.length < 2) continue;

      const actions = runnableIds.map((id) => {
        const linkedBt = selection.find((s) => s.reportPickId === id || s.bundleId === id)?.backtestId;
        const apiPick =
          response.picks.find((p) => p.pick_id === id) ??
          (linkedBt
            ? response.picks.find((p) => p.pick_id === `bt-${linkedBt}` || p.backtest_id === linkedBt)
            : undefined);
        return (
          actionByPickId[id] ??
          (linkedBt ? actionByPickId[`bt-${linkedBt}`] : undefined) ??
          apiPick?.action ??
          defaultRegimeAction
        );
      });
      const vetoCount = actions.filter((a) => a === "veto").length;
      const action: PolicyPickOverlapAction =
        vetoCount >= actions.length - vetoCount ? "veto" : "keep_only";

      const policyName = runnableIds
        .map(
          (id) =>
            selectionLabelByPickId.get(id) ||
            pickLabels.get(id) ||
            response.picks.find((p) => p.pick_id === id)?.label?.trim() ||
            id
        )
        .filter(Boolean)
        .join(" + ");

      out.push({
        pickIds: runnableIds,
        action,
        title: policyName
          ? `Overlap #${suggestion.rank} · ${policyName}`
          : `Overlap #${suggestion.rank}`,
        rank: suggestion.rank,
      });
    }
    return out;
  }, [
    actionByPickId,
    defaultRegimeAction,
    pickLabels,
    resolveRunnableIds,
    response,
    selectedSuggestionRanks,
    selection,
    selectionLabelByPickId,
  ]);

  const canRunSuggestionSims =
    !isExitPlacementMode &&
    Boolean(props.onRunSuggestionSims) &&
    suggestionRunCombos.length > 0;

  const handleRunCombinedSim = useCallback(() => {
    if (!selectedComboPickIds?.length || !canRunCombined) return;
    const runnableIds = resolveRunnableIds(selectedComboPickIds);
    if (runnableIds.length < 2) return;
    if (isExitPlacementMode) {
      props.onRunCombinedExitPlacementSim?.(
        runnableIds.filter((id) => reportPickIdSet.has(id))
      );
      return;
    }
    const leafIds = runnableIds.filter((id) => reportPickIdSet.has(id));
    const action =
      (leafIds[0] ? actionByPickId[leafIds[0]] : undefined) ??
      actionByPickId[runnableIds[0]!] ??
      defaultRegimeAction;
    props.onRunCombinedSim?.(runnableIds, action);
  }, [
    actionByPickId,
    canRunCombined,
    defaultRegimeAction,
    isExitPlacementMode,
    props,
    reportPickIdSet,
    resolveRunnableIds,
    selectedComboPickIds,
  ]);

  const handleRunSuggestionSims = useCallback(() => {
    if (!canRunSuggestionSims || suggestionRunCombos.length === 0) return;
    props.onRunSuggestionSims?.(
      suggestionRunCombos.map(({ pickIds, action, title }) => ({ pickIds, action, title }))
    );
  }, [canRunSuggestionSims, props, suggestionRunCombos]);

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent
        data-policy-pick-overlap-dialog=""
        showCloseButton={false}
        className="flex max-h-[min(90vh,48rem)] flex-col gap-4 sm:max-w-3xl"
      >
        <DialogHeader className="shrink-0 gap-1.5 text-left">
          <DialogTitle className={FIELD_LABEL_CLASS}>
            {isExitPlacementMode ? "Compare exit placement picks" : "Compare policy picks"}
          </DialogTitle>
          <DialogDescription className={REPORTS_DETAIL_FIELD_DESC_CLASS}>
            {isExitPlacementMode
              ? "Overlap % is the main combine signal. Per-pick Δ PnL is from each saved sim — not a multi-rule merge. Use Run combined sim for true combined EP economics."
              : "Check overlap before combining Remove / Keep rules. Recommendations are review-only."}
          </DialogDescription>
        </DialogHeader>

        <div
          data-policy-pick-overlap-body=""
          className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto pe-1 font-[family-name:var(--font-primary)]"
        >
          {/* 1. Selection */}
          <Field className="gap-1.5">
            <div className="flex items-baseline justify-between gap-3">
              <FieldLabel className={FIELD_LABEL_CLASS}>Selected picks</FieldLabel>
              <span
                className={cn(
                  MUTED_CLASS,
                  "text-xs tabular-nums",
                  selection.length > MAX_POLICY_PICK_OVERLAP &&
                    "font-medium text-[color:var(--brand-glacial-red)]"
                )}
              >
                {selection.length} / {MAX_POLICY_PICK_OVERLAP}
              </span>
            </div>
            {selection.length === 0 ? (
              <FieldDescription className={REPORTS_DETAIL_FIELD_DESC_CLASS}>
                {showAddPicks
                  ? "Add picks below to compare overlap."
                  : "Add saved simulations below to compare overlap."}
              </FieldDescription>
            ) : (
              <>
                {selection.length > MAX_POLICY_PICK_OVERLAP ? (
                  <SimulatorAlert tone="warning">
                    Only the first {MAX_POLICY_PICK_OVERLAP} picks are used for overlap. Highlighted
                    picks are over the limit — remove them or reorder by removing earlier ones.
                  </SimulatorAlert>
                ) : null}
                <div
                  data-policy-pick-overlap-selection=""
                  className="flex flex-wrap items-center justify-start gap-2"
                >
                  {selection.map((item, index) => {
                    const overLimit = index >= MAX_POLICY_PICK_OVERLAP;
                    const reportPick =
                      item.sourceKind === "report_pick" && item.reportPickId
                        ? reportPicksById.get(item.reportPickId)
                        : undefined;
                    const showRegimeAction =
                      !isExitPlacementMode &&
                      ((item.sourceKind === "report_pick" &&
                        item.reportPickId != null &&
                        reportPicksById.has(item.reportPickId) &&
                        !isExitPlacementPick(reportPicksById.get(item.reportPickId)!)) ||
                        (item.sourceKind === "combined_bundle" && item.bundleId != null));
                    const actionPickId =
                      item.sourceKind === "combined_bundle"
                        ? item.bundleId
                        : item.reportPickId;
                    return (
                      <div
                        key={item.key}
                        data-overlap-pick-over-limit={overLimit ? "" : undefined}
                        className={cn(
                          "inline-flex max-w-full items-center gap-1.5 rounded-[6px] border border-solid bg-card py-1 pl-2.5 pr-1",
                          overLimit && "bg-[color-mix(in_oklab,var(--brand-glacial-red)_10%,transparent)]"
                        )}
                        style={{
                          boxSizing: "border-box",
                          borderWidth: 1,
                          borderStyle: "solid",
                          borderColor: overLimit ? "var(--brand-glacial-red)" : "#d3d3d3",
                          borderRadius: 6,
                        }}
                        title={
                          overLimit
                            ? `${item.label} — over the ${MAX_POLICY_PICK_OVERLAP}-pick limit (not used)`
                            : item.warning
                              ? `${item.label} — ${item.warning}`
                              : item.label
                        }
                      >
                        <span
                          className={cn(
                            "max-w-[14rem] truncate font-[family-name:var(--font-primary)] text-sm font-medium leading-none",
                            overLimit
                              ? "text-[color:var(--brand-glacial-red)]"
                              : "text-[color:var(--brand-tropical-surf)]"
                          )}
                        >
                          {item.label}
                        </span>
                        {overLimit ? (
                          <Badge
                            variant="outline"
                            className={cn(
                              POLICY_KIND_CHIP_CLASS,
                              "shrink-0 border-[color:var(--brand-glacial-red)] text-[color:var(--brand-glacial-red)]"
                            )}
                          >
                            Over limit
                          </Badge>
                        ) : null}
                        {showRegimeAction && !overLimit && actionPickId ? (
                          <div className="shrink-0" onPointerDown={(e) => e.stopPropagation()}>
                            <FieldSelectFit
                              id={`overlap-selection-action-${actionPickId}`}
                              value={actionByPickId[actionPickId] ?? defaultRegimeAction}
                              options={[...REGIME_ACTION_OPTIONS]}
                              onValueChange={(v) => {
                                if (v !== "veto" && v !== "keep_only") return;
                                setPickAction(actionPickId, v);
                              }}
                            />
                          </div>
                        ) : null}
                        <button
                          type="button"
                          aria-label={
                            overLimit
                              ? `Remove over-limit pick ${item.label}`
                              : `Remove ${item.label}`
                          }
                          className={cn(
                            "inline-flex size-6 shrink-0 items-center justify-center rounded-[4px] transition-colors hover:bg-muted",
                            overLimit
                              ? "text-[color:var(--brand-glacial-red)] hover:text-[color:var(--brand-glacial-red)]"
                              : "text-muted-foreground hover:text-foreground"
                          )}
                          onClick={() => removeSelection(item.key)}
                        >
                          <X className="size-3.5" aria-hidden />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </Field>

          {/* 2. Add sources — only the controls that match how overlap was opened */}
          {((showAddPicks && availableReportPicks.length > 0) ||
            (showAddSavedSims && availableBacktests.length > 0)) && (
            <div
              className={cn(
                "grid grid-cols-1 gap-4",
                showAddPicks && showAddSavedSims && "sm:grid-cols-2"
              )}
            >
              {showAddPicks && availableReportPicks.length > 0 ? (
                <Field className="gap-1.5">
                  <FieldLabel htmlFor="overlap-add-pick" className={FIELD_LABEL_CLASS}>
                    Add another pick
                  </FieldLabel>
                  <TradeFilterSelectFullWidth
                    key={addPickMenuKey}
                    id="overlap-add-pick"
                    value=""
                    emptyValueShowsPlaceholder
                    placeholder="Select pick…"
                    options={availableReportPicks.map((p) => ({
                      value: p.id,
                      label: p.label,
                    }))}
                    onValueChange={(id) => {
                      if (!id) return;
                      addReportPick(id);
                      setAddPickMenuKey((k) => k + 1);
                    }}
                  />
                </Field>
              ) : null}
              {showAddSavedSims && availableBacktests.length > 0 ? (
                <Field className="gap-1.5">
                  <FieldLabel htmlFor="overlap-add-sim" className={FIELD_LABEL_CLASS}>
                    Add saved sim
                  </FieldLabel>
                  <TradeFilterSelectFullWidth
                    key={addSimMenuKey}
                    id="overlap-add-sim"
                    value=""
                    emptyValueShowsPlaceholder
                    placeholder="Select simulation…"
                    options={availableBacktests.map((b) => ({
                      value: b.id,
                      label: `${b.title?.trim() || b.id.slice(0, 8)} · ${isExitPlacementBacktest(b) ? "Exit" : "Regime"}`,
                    }))}
                    onValueChange={(id) => {
                      if (!id) return;
                      addBacktest(id);
                      setAddSimMenuKey((k) => k + 1);
                    }}
                  />
                </Field>
              ) : null}
            </div>
          )}

          {/* 3. Regime options — actions are set per pick on impact cards */}

          {skippedNotices.length > 0 ? (
            <SimulatorAlert tone="warning">
              {skippedNotices.map((s) => `${s.label}: ${s.reason}`).join(" · ")}
            </SimulatorAlert>
          ) : null}

          {/* 5. Results */}
          {response ? (
            <>
              <Separator />

              {driftWarnings.length > 0 ? (
                <SimulatorAlert tone="warning">
                  Report data may have changed since a saved sim — re-run sim to confirm.{" "}
                  {driftWarnings.map((d) => d.warning).join(" ")}
                </SimulatorAlert>
              ) : null}

              {response.disclaimer ? (
                <FieldDescription className={REPORTS_DETAIL_FIELD_DESC_CLASS}>
                  {response.disclaimer}
                </FieldDescription>
              ) : null}

              <Field className="gap-1.5">
                <FieldLabel className={FIELD_LABEL_CLASS}>Baseline cohort</FieldLabel>
                <p className={`m-0 ${BODY_CLASS}`}>{baselineSummaryLine(response.baseline)}</p>
              </Field>

              <Field className="gap-1.5">
                <FieldLabel className={FIELD_LABEL_CLASS}>Per-pick impact</FieldLabel>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {response.picks.map((p) => {
                    const impact = p.impact;
                    const isExit =
                      isExitPlacementPickResult(p) || isExitPlacementMode;
                    const kindBadge = isExit
                      ? overlapImpactKindBadgeLabel(
                          { ...p, sim_type: "exit_placement" },
                          resolveExitAvailabilityForPick(p.pick_id, p.backtest_id)
                        )
                      : null;
                    const regimeAction =
                      actionByPickId[p.pick_id] ??
                      (p.action === "keep_only" ? "keep_only" : defaultRegimeAction);
                    const selectionMatchLabel =
                      selectionLabelByPickId.get(p.pick_id) ??
                      (p.backtest_id ? selectionLabelByPickId.get(p.backtest_id) : undefined);
                    const displayLabel = resolveOverlapPickDisplayLabel(p.label, selectionMatchLabel);
                    const impactSummary = isExit
                      ? exitPlacementImpactSummary(impact)
                      : pickResultImpactSummary(p);
                    return (
                      <div
                        key={p.pick_id}
                        className={cn(SECTION_PANEL_CLASS, "flex flex-col gap-2")}
                      >
                        <p
                          className="m-0 line-clamp-3 whitespace-normal break-words font-[family-name:var(--font-primary)] text-sm font-medium text-[color:var(--brand-tropical-surf)]"
                          title={displayLabel}
                        >
                          {displayLabel}
                        </p>
                        <p className={`m-0 ${MUTED_CLASS}`}>{impactSummary}</p>
                        <div className="mt-auto flex items-end justify-between gap-2 pt-1">
                          <div className="min-w-0 flex-1">
                            {isExit ? (
                              <>
                                <p
                                  className={cn(
                                    "m-0 font-[family-name:var(--font-primary)] text-sm",
                                    impact.net_pnl_delta_if_applied >= 0
                                      ? "text-[color:var(--tessera-pnl-positive)]"
                                      : "text-[color:var(--brand-glacial-red)]"
                                  )}
                                >
                                  {exitPlacementDeltaPnlLine(impact)}
                                </p>
                                {isExitPlacementImpact(impact) ? (
                                  <p className={`m-0 ${MUTED_CLASS}`}>
                                    {impact.loss_improved_count} losses improved ·{" "}
                                    {impact.win_harmed_count} wins harmed
                                  </p>
                                ) : null}
                              </>
                            ) : (
                              <p
                                className={cn(
                                  "m-0 font-[family-name:var(--font-primary)] text-sm",
                                  impact.net_pnl_delta_if_applied >= 0
                                    ? "text-[color:var(--tessera-pnl-positive)]"
                                    : "text-[color:var(--brand-glacial-red)]"
                                )}
                              >
                                Δ PnL if applied {formatOverlapMoney(impact.net_pnl_delta_if_applied)}
                              </p>
                            )}
                          </div>
                          {isExit ? (
                            <Badge variant="outline" className={cn(POLICY_KIND_CHIP_CLASS, "shrink-0")}>
                              {kindBadge}
                            </Badge>
                          ) : p.pick_id.startsWith("bt-") ? (
                            <Badge variant="outline" className={cn(POLICY_KIND_CHIP_CLASS, "shrink-0")}>
                              {overlapImpactKindBadgeLabel(p)}
                            </Badge>
                          ) : (
                            <div className="shrink-0" onPointerDown={(e) => e.stopPropagation()}>
                              <FieldSelectFit
                                id={`overlap-pick-action-${p.pick_id}`}
                                value={regimeAction}
                                options={[...REGIME_ACTION_OPTIONS]}
                                onValueChange={(v) => {
                                  if (v !== "veto" && v !== "keep_only") return;
                                  setPickAction(p.pick_id, v);
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Field>

              {response.pairwise.length > 0 ? (
                <Field className="gap-1.5">
                  <FieldLabel className={FIELD_LABEL_CLASS}>Pairwise overlap</FieldLabel>
                  <FieldDescription className={REPORTS_DETAIL_FIELD_DESC_CLASS}>
                    {isExitPlacementMode
                      ? "If run alone = exits changed by that pick’s saved sim. Shared = exits changed by both. Exclusive = only one pick. Overlap % is the combine signal — run combined sim for multi-rule PnL."
                      : "If run alone = full sim for one pick. Exclusive = trades that pick removes but the other does not. Shared = removed by both."}
                  </FieldDescription>
                  <div className="flex flex-col gap-3">
                    {response.pairwise.map((row) => {
                      const b = pairwiseOverlapBreakdown(row, response.picks);
                      const pickALabel = pickLabels.get(row.pick_a) ?? b.pickALabel;
                      const pickBLabel = pickLabels.get(row.pick_b) ?? b.pickBLabel;
                      const isEpPair = b.isExitPlacement || isExitPlacementMode;
                      const aloneVerb = isEpPair ? "exits changed" : "trades";
                      const metrics: Array<[string, string]> = [
                        [
                          `${pickALabel} — if run alone`,
                          `${b.pickAStandaloneTrades} ${aloneVerb} · ${b.pickAStandaloneLosses} losses · ${b.pickAStandaloneWins} wins`,
                        ],
                        [
                          `${pickBLabel} — if run alone`,
                          `${b.pickBStandaloneTrades} ${aloneVerb} · ${b.pickBStandaloneLosses} losses · ${b.pickBStandaloneWins} wins`,
                        ],
                        [
                          isEpPair ? "Shared (both picks change exit)" : "Shared (both picks)",
                          `${b.sharedTrades} trades · ${b.sharedLosses} losses · ${b.sharedWins} wins`,
                        ],
                        [
                          isEpPair
                            ? "Combined union (either pick changes exit)"
                            : "Combined union (either pick)",
                          `${b.unionTrades} trades · ${b.unionLosses} losses · ${b.unionWins} wins`,
                        ],
                        [
                          `${pickALabel} exclusive (not in ${pickBLabel})`,
                          `${b.pickAExclusiveLosses} losses · ${b.pickAExclusiveWins} wins`,
                        ],
                        [
                          `${pickBLabel} exclusive (not in ${pickALabel})`,
                          `${b.pickBExclusiveLosses} losses · ${b.pickBExclusiveWins} wins`,
                        ],
                      ];
                      return (
                        <div key={`${row.pick_a}-${row.pick_b}`} className={SECTION_PANEL_CLASS}>
                          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                            <p className={`m-0 ${BODY_CLASS}`}>
                              <span className="font-medium">{pickALabel}</span>
                              <span className={MUTED_CLASS}> vs </span>
                              <span className="font-medium">{pickBLabel}</span>
                            </p>
                            <Badge
                              variant="outline"
                              className="border-transparent"
                              style={{ backgroundColor: overlapCellColor(b.overlapPct) }}
                            >
                              {formatOverlapPct(b.overlapPct)} overlap
                            </Badge>
                          </div>
                          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            {metrics.map(([label, value]) => (
                              <div key={label} className="flex flex-col gap-0.5">
                                <span className={cn(MUTED_CLASS, "text-xs")}>{label}</span>
                                <span className={BODY_CLASS}>{value}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Field>
              ) : null}

              {response.combination_suggestions.length > 0 ? (
                <Field className="gap-1.5">
                  <FieldLabel className={FIELD_LABEL_CLASS}>Top suggestions</FieldLabel>
                  <FieldDescription className={REPORTS_DETAIL_FIELD_DESC_CLASS}>
                    Check one or more suggestions. Selected picks update the combination preview.
                    Use Run selected suggestion(s) to simulate each checked suggestion.
                  </FieldDescription>
                  <div className="flex flex-col gap-2">
                    {response.combination_suggestions.map((s) => {
                      const checked = selectedSuggestionRanks.has(s.rank);
                      return (
                        <div
                          key={s.rank}
                          role="button"
                          tabIndex={0}
                          className={cn(
                            "flex cursor-pointer items-start gap-3 rounded-[6px] border border-solid p-3 text-left transition-colors",
                            checked
                              ? "border-[#38BBD1] bg-[color-mix(in_oklab,#38BBD1_8%,transparent)]"
                              : "border-border bg-card hover:border-[#38BBD1]/50"
                          )}
                          onClick={() => toggleSuggestionRank(s.rank, !checked)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              toggleSuggestionRank(s.rank, !checked);
                            }
                          }}
                        >
                          <div
                            data-trade-log-row-checkbox=""
                            className="flex shrink-0 pt-0.5"
                            onClick={(e) => e.stopPropagation()}
                            onPointerDown={(e) => e.stopPropagation()}
                          >
                            <Checkbox
                              data-trade-log-table-checkbox=""
                              checked={checked}
                              onCheckedChange={(value) =>
                                toggleSuggestionRank(s.rank, Boolean(value))
                              }
                              aria-label={`Select suggestion #${s.rank}`}
                            />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="mb-1 flex items-start justify-between gap-2">
                              <span className={cn(BODY_CLASS, "font-medium")}>
                                #{s.rank} ·{" "}
                                {s.pick_ids.map((id) => pickLabels.get(id) ?? id).join(" + ")}
                              </span>
                              <span
                                className={cn(MUTED_CLASS, "shrink-0 text-xs")}
                                title={complementarityTooltip(s.scores)}
                              >
                                Score {s.scores.composite_score.toFixed(2)}
                              </span>
                            </div>
                            <p className={`m-0 ${MUTED_CLASS}`}>{s.rationale}</p>
                            <p className={`m-0 mt-1 ${BODY_CLASS}`}>{suggestionImpactLine(s)}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Field>
              ) : null}

              {activeBundle ? (
                <Field className="gap-1.5">
                  <FieldLabel className={FIELD_LABEL_CLASS}>Combination preview</FieldLabel>
                  {bundlePreviewHasBlockingError(activeBundle) ? (
                    <SimulatorAlert tone="error">
                      {activeBundle.errors.find((e) => e.code === "unsatisfiable_keep_and")
                        ?.message ?? "This Keep + Keep combination cannot be applied."}
                    </SimulatorAlert>
                  ) : null}
                  <div className={SECTION_PANEL_CLASS}>
                    <div className="mb-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="flex flex-col gap-0.5">
                        <span className={cn(MUTED_CLASS, "text-xs")}>Combined gate (actual)</span>
                        <span className={BODY_CLASS}>
                          {entryFilterImpactSummary(
                            activeBundle.bundle_combined_impact,
                            activeBundle.actions[0] ?? "veto"
                          )}
                        </span>
                        <span
                          className={cn(
                            "font-[family-name:var(--font-primary)] text-sm",
                            activeBundle.bundle_combined_impact.net_pnl_delta_if_applied >= 0
                              ? "text-[color:var(--tessera-pnl-positive)]"
                              : "text-[color:var(--brand-glacial-red)]"
                          )}
                        >
                          Δ{" "}
                          {formatOverlapMoney(
                            activeBundle.bundle_combined_impact.net_pnl_delta_if_applied
                          )}
                        </span>
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <span className={cn(MUTED_CLASS, "text-xs")}>
                          Naive union (veto+veto sanity check)
                        </span>
                        <span className={BODY_CLASS}>
                          {entryFilterImpactSummary(
                            activeBundle.union_naive_impact,
                            activeBundle.actions[0] ?? "veto"
                          )}
                        </span>
                      </div>
                    </div>

                    {activeBundle.per_pick_marginal.length > 0 ? (
                      <div data-shadcn-data-table="" className="mb-3 w-full min-w-0 overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="text-[color:var(--brand-tropical-surf)]">
                                Pick
                              </TableHead>
                              <TableHead className="text-right text-[color:var(--brand-tropical-surf)]">
                                If run alone — losses
                              </TableHead>
                              <TableHead className="text-right text-[color:var(--brand-tropical-surf)]">
                                If run alone — wins
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {activeBundle.per_pick_marginal.map((m) => (
                              <TableRow key={m.pick_id}>
                                <TableCell>{pickLabelById(m.pick_id, response.picks)}</TableCell>
                                <TableCell className="text-right">
                                  {m.alone_affected_losses}
                                </TableCell>
                                <TableCell className="text-right">
                                  {m.alone_affected_wins}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    ) : null}

                    {activeBundle.incremental_impact ? (
                      <p className={`m-0 ${BODY_CLASS}`}>
                        Exclusive to picks after{" "}
                        {pickLabelById(
                          activeBundle.incremental_impact.beyond_pick_id,
                          response.picks
                        )}{" "}
                        in this combo: {activeBundle.incremental_impact.incremental_losses} losses,{" "}
                        {activeBundle.incremental_impact.incremental_wins} wins (
                        {activeBundle.incremental_impact.trades_affected_beyond_first} trades)
                      </p>
                    ) : null}

                    <p className={`m-0 mt-2 ${MUTED_CLASS}`}>{activeBundle.rationale}</p>
                  </div>
                </Field>
              ) : null}
            </>
          ) : null}
        </div>

        <DialogFooter className="shrink-0 sm:justify-end">
          <Button
            type="button"
            variant="outline"
            data-font="primary"
            className="normal-case tracking-normal"
            onClick={() => props.onOpenChange(false)}
          >
            Close
          </Button>
          <Button
            type="button"
            variant={canRunCombined || canRunSuggestionSims ? "outline" : "gradient"}
            data-font="primary"
            className="normal-case tracking-normal"
            onClick={() => void runOverlapCheck()}
            disabled={loading || selection.length < 1}
          >
            {loading ? (
              <>
                <Loader2 className="size-4 shrink-0 animate-spin" aria-hidden />
                Checking…
              </>
            ) : (
              "Check overlap"
            )}
          </Button>
          {canRunSuggestionSims ? (
            <Button
              type="button"
              variant="gradient"
              data-font="primary"
              className="normal-case tracking-normal"
              onClick={handleRunSuggestionSims}
            >
              {suggestionRunCombos.length === 1
                ? "Run selected suggestion"
                : `Run ${suggestionRunCombos.length} suggestions`}
            </Button>
          ) : null}
          {canRunCombined && !canRunSuggestionSims ? (
            <Button
              type="button"
              variant="gradient"
              data-font="primary"
              className="normal-case tracking-normal"
              onClick={handleRunCombinedSim}
            >
              Run combined sim
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { overlapPickIdsFromSelection };
